// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { NavigatorContext } from "@coveo/headless-react/ssr-commerce";
import { getSession } from "@insite/client-framework/ServerSideRendering";
import { v4 } from "uuid";
/**
 * Implements the Coveo NavigatorContext interface to provide navigation context
 * for server-side and client-side rendering.
 *
 * This class provides access to key browser/request information needed by Coveo:
 * - referrer: Previous page URL
 * - userAgent: Browser user agent string
 * - location: Current page URL
 * - clientId: Unique visitor ID for analytics
 *
 * The implementation handles both server-side and client-side contexts:
 * - Server-side: Gets values from request headers via getSession()
 * - Client-side: Gets values from browser APIs (document, window, navigator)
 *
 * The clientId is currently generated as a UUID if not found in cookies.
 * TODO: Update clientId handling based on cookie consent.
 */

export class CoveoNavigatorContext implements NavigatorContext {
    private isServerSide: boolean;

    constructor() {
        this.isServerSide = typeof window === "undefined";
    }

    get referrer(): string | null {
        const referrer = this.isServerSide
            ? getSession().headers?.["referer"]?.toString() || getSession().headers?.["referrer"]?.toString()
            : document.referrer;
        return referrer || null;
    }

    get userAgent(): string | null {
        const userAgent = this.isServerSide ? getSession().headers?.["user-agent"]?.toString() : navigator.userAgent;
        return userAgent || null;
    }

    get location(): string | null {
        const url = this.isServerSide ? getSession().url?.href : window.location.href;
        return url || null;
    }

    get clientId(): string {
        const visitorId = this.isServerSide
            ? getSession().headers?.["x-coveo-client-id"]?.toString()
            : document.cookie
                  .split("; ")
                  .find(row => row.startsWith("coveo_visitorId"))
                  ?.split("=")[1];

        // console.log("COOKIES >>", this.isServerSide, !this.isServerSide ? document.cookie : getSession().headers?.['cookie'])
        // console.log("VISITOR ID >>", visitorId)

        // TODO: If a cookie exists, we should use that as the clientId
        // If not, we should generate a new clientId if they have consented to cookies
        // If they have not consented to cookies, we should pass an empty string
        const clientId = visitorId || v4(); // is temporary until we can pass an empty string

        return clientId;
    }

    get marshal(): NavigatorContext {
        const clientId = this.clientId;
        const capture = clientId ? true : false;
        // console.log('ClientId >>', clientId, capture, this.isServerSide)
        return {
            clientId,
            location: this.location,
            referrer: this.referrer,
            userAgent: this.userAgent,
            capture,
        };
    }
}
